import { get, keyBy } from "lodash";
import Prismic from "prismic-javascript";

import { DEFAULT_COUNTRY_ISO } from "@stadium/constants";
import { client as prismicClient, prismicDocTypes } from "@sendmagic/prismic-nextjs/src/constants/prismic";
import { parsePrismicProgramConfig } from "@sendmagic/prismic-nextjs/src/helpers/prismic";

const {
  footer,
  useCasePages,
  featuredMenuBar,
  headerLinks,
  headerBanner,
  bookACall,
  contactUs,
  productShowAboutSection,
  marketingBanner,
  smHeaderLinks,
  smHeaderBanner,
  smFooter,
  getAQuote,
  searchPage,
  smGetAQuote,
  treat_dashboard,
  stadiumWallet,
  swagBookACall,
  pricingType,
  stadiumBookACall,
} = prismicDocTypes;

export const fetchPrismicData = async ({
  predicates = "document.type",
  scope = [],
  pageSize = 100,
  ref,
}) => {
  const data = await prismicClient.query([Prismic.Predicates.any(predicates, scope)], {
    pageSize,
    ref,
  });
  return data;
};

const fetchPrismicPageContent = async (prismicDocType) => {
  const data = await prismicClient.query(
    [Prismic.Predicates.at("document.type", prismicDocType)],
    {},
  );
  return data;
};

export const fetchFooterUrls = async () => {
  const data = await fetchPrismicData({ predicates: "document.type", scope: [footer] });
  const footerData = keyBy(data.results, (prismicPage) => prismicPage.type);
  return footerData;
};

export const fetchContactUsPage = async (app) => {
  const uid = `${app}-${DEFAULT_COUNTRY_ISO}`.toLowerCase();
  return await prismicClient.getByUID(contactUs, uid);
};

export const fetchLocalizedDoc = async ({
  docType,
  userCountry = {},
  defaultIntlUid = "intl",
  intlUid = defaultIntlUid,
}) => {
  const fetchDoc = (countryUid = defaultIntlUid) => prismicClient.getByUID(docType, countryUid);
  const { enabledPricedMenu, international, iso } = userCountry;
  const userCountryISO = enabledPricedMenu ? iso : intlUid;
  const uid = (international ? userCountryISO : DEFAULT_COUNTRY_ISO).toLowerCase();
  return (await fetchDoc(uid)) || (await fetchDoc());
};

export const fetchBookACallPage = async (
  app,
  countryCode = DEFAULT_COUNTRY_ISO,
  queryOptions = {},
) => {
  const uid =
    countryCode === DEFAULT_COUNTRY_ISO ? `${app}-${DEFAULT_COUNTRY_ISO}` : `${app}-other`;

  const query = [
    Prismic.Predicates.at("document.type", bookACall),
    Prismic.Predicates.any(`my.${bookACall}.app`, [app]),
    Prismic.Predicates.at(`my.${bookACall}.uid`, `${uid.toLocaleLowerCase()}`),
  ];
  const response = await prismicClient.query(query, queryOptions);
  return get(response, "results[0]");
};

export const fetchGetAQuotePage = async (app, queryOptions = {}) => {
  const query = [
    Prismic.Predicates.at("document.type", getAQuote),
    Prismic.Predicates.any(`my.${getAQuote}.app`, [app]),
  ];

  const response = await prismicClient.query(query, queryOptions);
  return get(response, "results[0]");
};

export const fetchPricingTypePage = async (slug, app, queryOptions = {}) => {
  const query = [
    Prismic.Predicates.at("document.type", pricingType),
    Prismic.Predicates.any(`my.${pricingType}.url`, [slug || "index"]),
    Prismic.Predicates.any(`my.${pricingType}.app`, [app]),
  ];

  const response = await prismicClient.query(query, queryOptions);
  return get(response, "results[0]");
};

export const fetchUseCasePages = async (slug, app, queryOptions = {}) => {
  const query = [
    Prismic.Predicates.at("document.type", useCasePages),
    Prismic.Predicates.any("my.use_case_pages.url", [slug || "index"]),
    Prismic.Predicates.any("my.use_case_pages.app", [app]),
  ];
  const response = await prismicClient.query(query, queryOptions);
  return get(response, "results[0]");
};

export const fetchDefaultPrismicContent = async (queryOptions = {}) => {
  const response = await fetchPrismicData({
    predicates: "document.type",
    scope: [
      headerLinks,
      footer,
      smFooter,
      headerBanner,
      productShowAboutSection,
      marketingBanner,
      smHeaderLinks,
      smHeaderBanner,
    ],
    ref: queryOptions?.ref,
  });
  return response;
};

export const fetchSharedPrismicContent = async (queryOptions = {}, prismicAppName) => {
  const response = await fetchDefaultPrismicContent(queryOptions);
  return JSON.parse(JSON.stringify(parsePrismicProgramConfig(response, prismicAppName)));
};

export const fetchMarketingBanner = (app, successCb, queryOptions = {}) => {
  const query = [
    Prismic.Predicates.at("document.type", marketingBanner),
    Prismic.Predicates.any("my.marketing_banner.app", [app]),
  ];
  prismicClient
    .query(query, queryOptions)
    .then((response) => {
      const doc = get(response, "results[0]");
      if (doc) {
        successCb(doc);
      }
    })
    .catch(() => {
      // eslint-disable-next-line no-console
      console.error("Unable to load marketing banner from prismic");
    });
};

export const fetchAllUseCasePages = async (queryOptions, app) => {
  const query = [
    Prismic.Predicates.at("document.type", useCasePages),
    Prismic.Predicates.any("my.use_case_pages.app", [app]),
  ];
  const response = await prismicClient.query(query, queryOptions);
  return response;
};

export const getPrismicPages = async (queryOptions) => {
  const prismicDocTypeValues = Object.values(prismicDocTypes);
  const staticPageTypes = prismicDocTypeValues.filter((type) => type !== useCasePages);

  const query = [Prismic.Predicates.any("document.type", staticPageTypes)];
  const response = await prismicClient.query(query, queryOptions);
  return response;
};

export const fetchFeaturedMenuBar = async (countryISO) => {
  const data = await prismicClient.getByUID(featuredMenuBar, countryISO);
  return data;
};

export const fetchSearchPage = async () => {
  const data = await fetchPrismicPageContent(searchPage);
  return get(data, "results[0]");
};

export const fetchSMGetAQuotePage = async () => {
  const data = await fetchPrismicPageContent(smGetAQuote);
  return get(data, "results[0]");
};

export const fetchWalletContent = async (app) => {
  const uid = `${app}-wt`;
  return await prismicClient.getByUID(stadiumWallet, uid);
};

export const fetchSwagBookACallPage = async (countryISO, queryOptions = {}) => {
  const uid = countryISO.toLowerCase();
  const query = [
    Prismic.Predicates.at("document.type", swagBookACall),
    Prismic.Predicates.at(`my.${swagBookACall}.uid`, uid),
  ];
  const response = await prismicClient.query(query, queryOptions);
  return get(response, "results[0]");
};

export const fetchMarketingBannerByUID = async (uid) =>
  await prismicClient.getByUID(marketingBanner, uid);

export const fetchStadiumBookACall = async (uid) =>
  await prismicClient.getByUID(stadiumBookACall, uid);

export const fetchTreatDashboardContent = async (uid, queryOptions = {}) => {
  const query = [
    Prismic.Predicates.at("document.type", treat_dashboard),
    Prismic.Predicates.at(`my.${treat_dashboard}.uid`, uid),
  ];
  const response = await prismicClient.query(query, queryOptions);
  return get(response, "results[0]");
};

export const fetchStadiumLoggedOutNav = async (app) => {
  const query = [
    Prismic.Predicates.at("document.type", headerLinks),
    Prismic.Predicates.any(`my.${headerLinks}.app`, [app]),
  ];
  const response = await prismicClient.query(query);
  return get(response, "results[0]");
};

export const fetchStadiumMarketingBannerData = async (app = "stadium") => {
  try {
    const query = [
      Prismic.Predicates.at("document.type", headerBanner),
      Prismic.Predicates.any(`my.${headerBanner}.app`, [app]),
    ];
    const response = await prismicClient.query(query);
    return get(response, "results[0]");
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Unable to load stadium marketing banner from prismic", error);
    return null;
  }
};
